/**
 * Offer Card Component
 *
 * Dedicated component for 'offer' resource items (discounts, packages, deals).
 * Renders the page schema fields plus the offer-specific ones: validity period,
 * price badge, provider name and booking link.
 * Supports both feed card and map popup rendering via the `variant` prop.
 */

import { h } from 'preact';
import htm from 'htm';
import { IconCalendar } from '../../../components/Icons.js';
import { CardMedia } from './shared/CardMedia.js';
import { CardDescription } from './shared/CardDescription.js';
import { CardLinks } from './shared/CardLinks.js';
import { pickText, label } from './shared/fields.js';
import { formatDateRange } from '../../../utils/dateFormatters.js';

const html = htm.bind(h);

/**
 * Renders an offer item as a result card or map popup.
 *
 * @param {Object} props
 * @param {Object} props.item - The offer item data.
 * @param {Object} [props.block] - Parent block data.
 * @param {Object} props.ctx - View context with `t` and `config`.
 * @param {string} [props.variant='card'] - 'card' for blocks feed or 'popup' for map infowindow.
 * @param {Function} [props.onClose] - Callback when the popup close button is clicked.
 */
export function OfferCard({ item, block, ctx, variant = 'card', onClose }) {
  const isPopup = variant === 'popup';

  const tag = pickText(item, 'categories', 'category', 'tag') || label(ctx, 'type_offer', 'Offer');
  const name = pickText(item, 'name', 'label', 'title');
  const price = pickText(item, 'price');
  const validity = formatDateRange(pickText(item, 'start_date'), pickText(item, 'end_date'), {
    from: label(ctx, 'valid_from', 'Valid from {date}'),
    until: label(ctx, 'valid_until', 'Valid until {date}')
  });
  const provider = pickText(item, 'provider_name');
  const description = pickText(item, 'description', 'content');
  const bookingUrl = pickText(item, 'booking_url');
  const website = pickText(item, 'website');
  const sourceUrl = pickText(item, 'source_url', 'url');

  const containerClass = isPopup
    ? 'mta-result-card mta-card--popup mta-map-popup-card mta-map-popup-theme'
    : 'mta-result-card';

  return html`
    <div class="${containerClass}">
      <${CardMedia} item=${item} block=${block} ctx=${ctx} variant=${variant} onClose=${onClose} />

      <div class="mta-card-tag-row">
        <div class="mta-card-tag">${tag}</div>
        ${price ? html`<span class="mta-card-badge" title="Price">${price}</span>` : ''}
      </div>

      <h3 class="mta-card-title">${name}</h3>

      ${validity || provider
        ? html`
            <div class="mta-card-meta">
              ${validity
                ? html`
                    <div class="mta-card-meta-item mta-meta-date" title="Validity">
                      <span style="display:inline-flex; align-items:center; color:var(--mta-text-soft);"><${IconCalendar} /></span>
                      <span>${validity}</span>
                    </div>
                  `
                : ''}
              ${provider
                ? html`
                    <div class="mta-card-meta-item" title="Provider">
                      <span>${label(ctx, 'offered_by', 'Offered by {name}').replace('{name}', provider)}</span>
                    </div>
                  `
                : ''}
            </div>
          `
        : ''}

      <${CardDescription} description=${description} ctx=${ctx} variant=${variant} />

      <${CardLinks} links=${[
        { href: bookingUrl, text: label(ctx, 'get_offer', 'Get offer') },
        { href: sourceUrl, text: label(ctx, 'show_on_site', 'Show on site') },
        { href: website, text: label(ctx, 'website', 'Website') }
      ]} />
    </div>
  `;
}
